import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage, useFormikContext, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import TimezoneSelect, { allTimezones } from 'react-timezone-select';
import moment from 'moment-timezone';
import { useCreateGroup } from '../../providers/CreateGroupProvider';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useAgentHeartBeatService } from '../../components/services/agentHeartBeatService';
import { AgentStatusIndicator } from '../../components/AgentStatus';
import api, { noBrowserCache } from '../../components/services/apiService';
import Pagination from '../../components/Pagination/Pagination';
import { usePermissions } from '../../hooks/usePermissions';

type SelectOption = {
  label: string;
  value: string;
};

type FormValues = {
  planName: string;
  timezone: string;
  timezoneValue: string;
};

// Helper to parse distinguished names
export function parseDistinguishedName(dn: string) {
  const parts = dn.split(',').map((p) => p.trim());
  const dcParts = parts.filter((p) => p.startsWith('DC=')).map((p) => p.substring(3));
  const ouParts = parts.filter((p) => p.startsWith('OU=')).map((p) => p.substring(3));

  return {
    domain: dcParts.join('.'), // e.g., "patchifi.local"
    domainComponents: dcParts,
    firstOU: ouParts[0] || null, // First OU after CN
    fullDN: dn,
  };
}

export const getUTCOffset = (timezone: string): string => {
  try {
    const date = new Date();
    const localeTime = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);

    const localHours = parseInt(localeTime.find((part) => part.type === 'hour')?.value ?? '0', 10);
    const localMinutes = parseInt(localeTime.find((part) => part.type === 'minute')?.value ?? '0', 10);

    const utcHours = date.getUTCHours();
    const utcMinutes = date.getUTCMinutes();

    const offsetHours = localHours - utcHours;
    const offsetMinutes = localMinutes - utcMinutes;

    let totalOffsetMinutes = offsetHours * 60 + offsetMinutes;

    if (totalOffsetMinutes <= -720) totalOffsetMinutes += 1440;
    if (totalOffsetMinutes >= 720) totalOffsetMinutes -= 1440;

    const sign = totalOffsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(totalOffsetMinutes);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const minutes = String(absOffset % 60).padStart(2, '0');

    return `UTC${sign}${hours}:${minutes}`;
  } catch {
    return 'UTC+00:00';
  }
};

export function offsetToTimezone(offset) {
  const normalizedOffset = offset.replace('UTC', '');

  return Object.keys(allTimezones).find((tz) => {
    const tzOffset = moment.tz(tz).format('Z'); // e.g. "-07:00"
    return tzOffset === normalizedOffset;
  });
}

const TimezoneField = ({ field, readOnly }: any) => {
  const { CreateGroupData } = useCreateGroup();
  const { setFieldValue, values } = useFormikContext<any>();
  const [selectedTimezone, setSelectedTimezone] = useState<any>({
    value: values.timezoneValue || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  useEffect(() => {
    setSelectedTimezone({
      value: CreateGroupData.timezoneValue || Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }, [CreateGroupData.timezoneValue]);

  return (
    <TimezoneSelect
      value={selectedTimezone}
      isDisabled={readOnly}
      onChange={(timezone: any) => {
        setSelectedTimezone(timezone);
        const utcOffset = getUTCOffset(timezone.value);
        setFieldValue('timezone', utcOffset); // offset string (for API)
        setFieldValue('timezoneValue', timezone.value); // actual zone (for restoring UI)
      }}
      styles={{
        control: (base, state) => ({
          ...base,
          backgroundColor: '#161617',
          borderColor: state.isFocused ? '#22c55e' : '#4b5563',
          boxShadow: 'none',
          color: '#fff',
          minHeight: 56,
          '&:hover': { borderColor: '#22c55e' },
        }),
        menu: (base) => ({
          ...base,
          backgroundColor: '#161617',
          color: '#fff',
        }),
        singleValue: (base) => ({
          ...base,
          color: '#fff',
        }),
        option: (base, { isFocused, isSelected }) => ({
          ...base,
          backgroundColor: isSelected ? '#444' : isFocused ? '#27272a' : 'transparent',
          color: '#fff',
          cursor: 'pointer',
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({
          ...base,
          color: '#fff',
          ':hover': { color: '#22c55e' },
        }),
      }}
    />
  );
};

const validationSchema = Yup.object({
  planName: Yup.string().required('Group Name is required'),
  timezone: Yup.string().required('Time zone is required'),
});

const PAGE_SIZE = 20;

const getOSAgents = async (osFlavoursArray: any[], auth: any, tenantId: string) => {
  try {
    if (auth?.userData) {
      const token = auth.userData.access_token;
      // osFlavoursArray is expected to be an array of { label, value, osType } or just string (osName)
      // Build the correct payload: { osFlavours: [{ osName, osType }, ...] }
      const osFlavoursPayload = {
        osFlavours: osFlavoursArray.map((item) =>
          typeof item === 'string'
            ? { osName: item, osType: item.includes('Server') ? 'Server' : 'Client' }
            : {
                osName: item.osName || item.value || item.label,
                osType: item.osType || (item.value && item.value.includes('Server') ? 'Server' : 'Client'),
              },
        ),
      };
      const response = await api.post(
        `${Project.api}tenantservice/TenantAgent/${tenantId}/agent-by-os-flavour`,
        JSON.stringify(osFlavoursPayload),
      );

      const { data } = response;
      return data?.isSuccess ? (Array.isArray(data.data) && data.data[0]?.agents ? data.data[0].agents : data.data || []) : [];
    }
  } catch (error) {
    console.error('Error fetching OS agents:', error);
  }
  return [];
};

const getActiveDirectoryAgents = async (fullDistinguishedNames: string[], auth: any, tenantId: string) => {
  try {
    if (auth?.userData) {
      const response = await api.post(
        `${Project.api}tenantservice/TenantAgent/${tenantId}/agent-by-active-directory-group?pageNumber=0&pageSize=20`,
        JSON.stringify(fullDistinguishedNames),
      );
      const respData = response?.data;
      if (!respData || !respData.isSuccess) return [];

      if (Array.isArray(respData.data)) {
        return respData.data.map((item: any) => ({
          tenantAgentId: item.agentId,
          agentId: item.agentId,
          deviceName: item.deviceName,
          fullDistinguishedName: item.fullDistinguishedName,
          groupName: item.groupName,
          sourceStatus: item.status,
          os: item.os || '-',
          osVersion: item.osVersion || '-',
        }));
      }
      return [];
    }
    return [];
  } catch (err) {
    console.error('Error fetching Active Directory agents:', err);
    return [];
  }
};

const getActiveDirectoryGroup = async (auth: any, tenantId: string) => {
  try {
    if (auth?.userData) {
      const response = await api.get(`${Project.api}tenantservice/TenantAgent/${tenantId}/agent-active-directory-group`);
      const { data } = response;
      const adData = data?.data || [];

      // Build domain map
      const domainMap: Record<string, { groups: Record<string, string[]> }> = {};
      adData.forEach((item: any) => {
        const dn = item.fullDistinguishedName;
        const parsed = parseDistinguishedName(dn);
        if (!parsed.domain || !parsed.firstOU) return;

        if (!domainMap[parsed.domain]) {
          domainMap[parsed.domain] = { groups: {} };
        }
        if (!domainMap[parsed.domain].groups[parsed.firstOU]) {
          domainMap[parsed.domain].groups[parsed.firstOU] = [];
        }
        domainMap[parsed.domain].groups[parsed.firstOU].push(dn);
      });

      return { domainMap, adData };
    }
    return { domainMap: {}, adData: [] };
  } catch (error) {
    console.error('Error fetching Active Directory data:', error);
    return { domainMap: {}, adData: [] };
  }
};

const CreateGroupForm = () => {
  const navigate = useNavigate();
  const { CreateGroupData, setCreateGroupData } = useCreateGroup();
  const [isGroupActive, setIsGroupActive] = useState<boolean>(true);
  const [searchCriteria, setSearchCriteria] = useState<'OS Flavour' | 'Active Directory'>('OS Flavour');
  const [searchValues, setSearchValues] = useState<SelectOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDevices, setShowDevices] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState<any[]>([]);
  const [osFlavour, setOSflavour] = useState<any[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { id } = useParams();
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const [heartbeatData, setHeartbeatData] = useState<Record<string, { status: string; ipAddress: string; heartbeatStatusMessage: string }>>(
    {},
  );
  const [heartbeatLoading, setHeartbeatLoading] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState(false);
  const { addToast } = useToast();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('groupPolicies');

  // NEW: Active Directory state
  const [activeDirectoryDomainMap, setActiveDirectoryDomainMap] = useState<Record<string, { groups: Record<string, string[]> }>>({});
  const [selectedADDomain, setSelectedADDomain] = useState<string | null>(null);
  const [selectedADGroups, setSelectedADGroups] = useState<string[]>([]);
  const [activeDirectoryRawData, setActiveDirectoryRawData] = useState<any[]>([]);
  //current group name
  const [currentGroupName, setCurrentGroupName] = useState<string>('');
  const defaultGroup = localStorage.getItem('defaultTenantGroupName') || '';

  /**
   * selectedDeviceOrigins stores metadata about where each selection came from.
   * - origin: 'existing' | 'manual'
   * - criteria: if origin === 'manual', this stores which criteria (OS Flavour | Active Directory)
   *
   * This allows pruning only the selections made during this edit session that belong to a different criteria
   * when the user switches search criteria.
   */
  const [selectedDeviceOrigins, setSelectedDeviceOrigins] = useState<
    Record<string, { origin: 'existing' | 'manual'; criteria?: 'OS Flavour' | 'Active Directory' }>
  >({});

  // helper to normalize id field from different payload shapes
  const normalizeId = (device: any) => device?.agentId || device?.tenantAgentId || device?.deviceId || device?.id || '';

  const getOSflavours = async () => {
    try {
      if (auth?.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;
        const response = await api.get(`${Project.api}tenantservice/TenantAgent/${tenantId}/os-flavour`);
        setOSflavour(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching OS flavours:', error);
    }
  };

  const getActiveDirectoryData = async () => {
    const { domainMap, adData } = await getActiveDirectoryGroup(auth, selectedTenantId as string);
    setActiveDirectoryDomainMap(domainMap);
    setActiveDirectoryRawData(adData);
  };

  useEffect(() => {
    getOSflavours();
    getActiveDirectoryData();
  }, []);

  const handleToggleActive = async () => {
    try {
      const token = auth?.userData?.access_token;
      const tenantId = selectedTenantId;
      const groupId = CreateGroupData?.TenantGroupId;

      const endpoint = isGroupActive
        ? `${Project.api}tenantservice/TenantGroup/${tenantId}/disable-tenant-group/${groupId}`
        : `${Project.api}tenantservice/TenantGroup/${tenantId}/enable-tenant-group/${groupId}`;

      await api.put(endpoint, '');

      setIsGroupActive((prev) => !prev);
      addToast(isGroupActive ? 'Group disabled successfully' : 'Group enabled successfully', 'success', 4000);

      setTimeout(() => {
        fetchGroupData();
        getSelectedTenantAgentIds();
      }, 3500);
    } catch (err) {
      console.error('Error toggling group status', err);
      addToast('Failed to update group status', 'error', 4000);
    }
  };

  const fetchHeartbeat = async (deviceIds: string[]) => {
    if (!deviceIds.length) return;
    setHeartbeatLoading(true);
    try {
      const data = await getAgentHeartbeatStatus(selectedTenantId as string, deviceIds);
      const heartbeatMap: Record<string, { status: string; ipAddress: string; heartbeatStatusMessage: string }> = {};
      data.data.forEach((agent: any) => {
        heartbeatMap[agent.tenantAgentId || agent.agentId] = {
          status: agent.agentStatus || (agent.status ? 'Active' : 'Inactive'),
          ipAddress: agent.ipAddress || '-',
          heartbeatStatusMessage:
            agent.heartbeatStatusMessage || agent.heartBeatStatusMessage || agent.statusMessage || 'No heartbeat status message available',
        };
      });
      setHeartbeatData(heartbeatMap);
    } catch (error) {
      console.error('Error fetching heartbeat:', error);
    } finally {
      setHeartbeatLoading(false);
    }
  };

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const token = auth?.userData?.access_token;
      const tenantId = selectedTenantId;
      const response = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/groups/${id}`, noBrowserCache());

      if (response.data?.isSuccess) {
        let group = response.data.data[0].tenantGroupList[0];
        // Sync the toggle with the stored state, otherwise a disabled group is shown as active
        // and the toggle calls disable-tenant-group again instead of enable-tenant-group.
        setIsGroupActive(group.isActive !== false);
        setCreateGroupData((prev: any) => ({
          ...prev,
          tenantGroupName: group.groupName,
          timezone: group.timezone,
          timezoneValue: group.timezone ? offsetToTimezone(group.timezone) : '',
          isDefault: group.isDefault,
          ...group,
        }));
      }
    } catch (error) {
      console.error('Error fetching group data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedTenantAgentIds = async () => {
    try {
      setLoading(true);
      const token = auth?.userData?.access_token;
      const tenantId = selectedTenantId;
      const response = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/agents/${id}`);

      if (response.data?.isSuccess) {
        setCurrentGroupName(response.data.data[0].groupName);

        let agents = response.data.data[0].agents;
        const normalized = agents.map((item: any) => ({
          agentId: item.tenantAgentId,
          deviceName: item.deviceName,
          groupName: item.groupName,
          os: item.os,
          osVersion: item.osVersion,
          status: item.isActive,
        }));

        // Prepopulate selected devices (existing group members)
        setSelectedDevices(normalized);

        // Merge existing saved IDs with any current selection (don't overwrite)
        setSelectedDeviceIds((prev) => {
          const incoming = agents.map((item: any) => item.tenantAgentId);
          const merged = Array.from(new Set([...prev, ...incoming]));
          return merged;
        });

        // Mark those as 'existing' origin
        setSelectedDeviceOrigins((prev) => {
          const copy = { ...prev };
          agents.forEach((a: any) => {
            const idn = a.tenantAgentId;
            copy[idn] = { origin: 'existing' };
          });
          return copy;
        });

        setShowDevices(true);
        fetchHeartbeat(agents.map((item: any) => item.tenantAgentId));
      }
    } catch (error) {
      console.error('Error fetching agents data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchGroupData();
      getSelectedTenantAgentIds();
      setCreateGroupData((prev: any) => ({ ...prev, TenantGroupId: id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auth]);

  const osOptions: SelectOption[] = osFlavour?.map((item: any) => ({
    label: item.osName,
    value: item.osName,
  }));

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: '#161617',
      borderColor: state.isFocused ? '#22c55e' : '#4B5563',
      boxShadow: 'none',
      minHeight: 56,
      '&:hover': { borderColor: '#22c55e' },
    }),
    menu: (base: any) => ({ ...base, backgroundColor: '#161617' }),
    option: (base: any, { isFocused, isSelected }: any) => ({
      ...base,
      backgroundColor: isSelected ? '#444' : isFocused ? '#27272a' : 'transparent',
      color: '#fff',
      cursor: 'pointer',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#2b2b2b',
      borderRadius: 8,
    }),
    multiValueLabel: (base: any) => ({ ...base, color: '#fff' }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: '#ef4444',
      ':hover': { backgroundColor: 'transparent', color: '#b91c1c' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({ ...base, color: '#fff' }),
  };

  /**
   * PRUNE selections when switching criteria:
   * - keep all 'existing' origins (they were originally saved in the group)
   * - keep manual selections that match the new criteria
   * - remove manual selections that belong to the previous criteria
   */
  const pruneSelectionsOnCriteriaChange = (newCriteria: 'OS Flavour' | 'Active Directory') => {
    setSelectedDeviceIds((prev) => {
      const kept = prev.filter((id) => {
        const meta = selectedDeviceOrigins[id];
        if (!meta) return true; // if unknown origin, keep to be safe
        if (meta.origin === 'existing') return true;
        if (meta.origin === 'manual' && meta.criteria === newCriteria) return true;
        return false; // remove manual items from other criteria
      });

      // also clean origins map
      setSelectedDeviceOrigins((orig) => {
        const updated: typeof orig = {};
        Object.keys(orig).forEach((k) => {
          if (kept.includes(k)) updated[k] = orig[k];
        });
        return updated;
      });

      return kept;
    });
  };

  // Called when user toggles selection checkbox/square
  const handleSquareClick = (device: any) => {
    const id = normalizeId(device);
    if (!id) return;

    setSelectedDeviceIds((prevSelected) => {
      const exists = prevSelected.includes(id);
      if (exists) {
        // remove
        setSelectedDeviceOrigins((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
        return prevSelected.filter((x) => x !== id);
      } else {
        // add -> mark as manual with current criteria
        setSelectedDeviceOrigins((prev) => ({
          ...prev,
          [id]: { origin: 'manual', criteria: searchCriteria },
        }));
        // append to existing selections (merge, avoid duplicates)
        return Array.from(new Set([...prevSelected, id]));
      }
    });
  };

  // When the user switches search criteria we prune only the manual selections from other criteria
  const handleCriteriaChange = (newCriteria: 'OS Flavour' | 'Active Directory') => {
    const prevCriteria = searchCriteria;
    if (newCriteria === prevCriteria) return;

    // Prune manual selections that belong to the previous criteria
    pruneSelectionsOnCriteriaChange(newCriteria);

    // Reset UI-specific filters and visible devices, but keep 'existing' selections
    setSearchCriteria(newCriteria);
    setSearchValues([]);
    setSelectedADGroups([]);
    setSelectedADDomain(null);
    setSelectedDevices([]);
    setShowDevices(false);
    setSelectAll(false);
  };

  const totalPages = selectedDevices ? Math.ceil(selectedDevices.length / PAGE_SIZE) : 1;
  const paginatedDevices = selectedDevices ? selectedDevices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) : [];

  useEffect(() => {
    if (CreateGroupData) {
      if (CreateGroupData.searchValues) setSearchValues(CreateGroupData.searchValues);
      if (CreateGroupData.selectedDevices) {
        setSelectedDevices(CreateGroupData.selectedDevices);
        setShowDevices(true);
        setCurrentPage(1); // Reset pagination when devices change
      }
      if (CreateGroupData.selectedDeviceIds) {
        // merge rather than overwrite, and mark merged ones as 'existing'
        setSelectedDeviceIds((prev) => {
          const merged = Array.from(new Set([...(prev || []), ...(CreateGroupData.selectedDeviceIds || [])]));
          return merged;
        });
        setSelectedDeviceOrigins((prev) => {
          const copy = { ...prev };
          (CreateGroupData.selectedDeviceIds as string[]).forEach((id: string) => {
            copy[id] = { origin: 'existing' };
          });
          return copy;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CreateGroupData]);

  // NEW: Get group options for AD based on domain selection
  const getADGroupOptions = () => {
    if (!activeDirectoryDomainMap) return [];

    const domains = Object.keys(activeDirectoryDomainMap);
    if (domains.length === 1) {
      // Single domain - show all groups
      const domain = domains[0];
      return Object.keys(activeDirectoryDomainMap[domain].groups).map((ou) => ({ label: ou, value: ou }));
    } else {
      // Multi domain - show groups for selected domain
      if (!selectedADDomain) return [];
      return Object.keys(activeDirectoryDomainMap[selectedADDomain].groups).map((ou) => ({ label: ou, value: ou }));
    }
  };

  // NEW: Handle AD group selection
  const handleADGroupChange = (selected: MultiValue<SelectOption>) => {
    setSelectedADGroups((selected as SelectOption[])?.map((s) => s.value) || []);
  };

  /**
   * Fetch visible devices based on current criteria and filters.
   * This is used by Show Devices and can be used to prune selections if needed.
   */
  const fetchVisibleDevicesForCurrentCriteria = async () => {
    if (searchCriteria === 'OS Flavour') {
      const selectedFlavours = searchValues.map((item) => item.value);
      if (selectedFlavours.length === 0) {
        // nothing selected -> return empty visible set
        return [];
      }
      const agents = await getOSAgents(selectedFlavours, auth, selectedTenantId as string);
      return agents;
    } else {
      // Active Directory
      if (selectedADGroups.length === 0) {
        return [];
      }
      let fullDNsToQuery: string[] = [];
      const domains = Object.keys(activeDirectoryDomainMap);

      if (domains.length === 1) {
        const domain = domains[0];
        fullDNsToQuery = selectedADGroups.flatMap((group) => activeDirectoryDomainMap[domain].groups[group] || []);
      } else {
        if (!selectedADDomain) {
          addToast('Please select a domain', 'error', 4000);
          return [];
        }
        fullDNsToQuery = selectedADGroups.flatMap((group) => activeDirectoryDomainMap[selectedADDomain].groups[group] || []);
      }

      if (fullDNsToQuery.length === 0) {
        return [];
      }

      const adAgents = await getActiveDirectoryAgents(fullDNsToQuery, auth, selectedTenantId as string);
      return adAgents;
    }
  };

  const handleShowDevices = async () => {
    try {
      // Clear previous visible devices and UI selection state (but keep selectedDeviceIds which include 'existing' ones)
      setSelectedDevices([]);
      setSelectAll(false);

      if (searchCriteria === 'OS Flavour') {
        const selectedFlavours = searchValues.map((item) => item.value);
        if (selectedFlavours.length === 0) {
          setShowDevices(true);
          setSelectedDevices([]);
          pruneSelectionsOnCriteriaChange(searchCriteria);
          return;
        }
        const agents = await getOSAgents(selectedFlavours, auth, selectedTenantId as string);
        const ungroupedAgents = agents.filter((d: any) => !d.groupName || d.groupName.trim() === '' || d.groupName == defaultGroup);
        setSelectedDevices(ungroupedAgents);
        fetchHeartbeat(agents.map((item: any) => normalizeId(item)));
      } else if (searchCriteria === 'Active Directory') {
        if (selectedADGroups.length === 0) {
          setShowDevices(true);
          setSelectedDevices([]);
          pruneSelectionsOnCriteriaChange(searchCriteria);
          return;
        }

        let fullDNsToQuery: string[] = [];
        const domains = Object.keys(activeDirectoryDomainMap);

        if (domains.length === 1) {
          const domain = domains[0];
          fullDNsToQuery = selectedADGroups.flatMap((group) => activeDirectoryDomainMap[domain].groups[group] || []);
        } else {
          if (!selectedADDomain) {
            addToast('Please select a domain', 'error', 4000);
            return;
          }
          fullDNsToQuery = selectedADGroups.flatMap((group) => activeDirectoryDomainMap[selectedADDomain].groups[group] || []);
        }

        if (fullDNsToQuery.length === 0) {
          addToast('No devices found for selected criteria', 'error', 4000);
          setSelectedDevices([]);
          pruneSelectionsOnCriteriaChange(searchCriteria);
          return;
        }

        const adAgents = await getActiveDirectoryAgents(fullDNsToQuery, auth, selectedTenantId as string);
        const defaultGroupLocal = localStorage.getItem('defaultTenantGroupName') || '';
        const ungroupedAgents = adAgents.filter((d: any) => !d.groupName || d.groupName.trim() === '' || d.groupName == defaultGroupLocal);
        setSelectedDevices(ungroupedAgents);
        fetchHeartbeat(adAgents.map((item: any) => normalizeId(item)));
      }

      setCurrentPage(1);
      setShowDevices(true);
    } catch (error) {
      console.error('Error in handleShowDevices:', error);
      addToast('Failed to fetch devices', 'error', 4000);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        planName: CreateGroupData?.tenantGroupName || '',
        timezone: CreateGroupData?.timezone || getUTCOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
        timezoneValue: CreateGroupData?.timezoneValue || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }}
      validationSchema={readOnly ? undefined : validationSchema}
      onSubmit={(values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
        setCreateGroupData((prev: any) => ({
          ...prev,
          tenantGroupName: values.planName,
          timezone: values.timezone,
          timezoneValue: values.timezoneValue,
          tenantAgentIds: selectedDeviceIds,
          searchCriteria,
          searchValues,
          selectedDevices,
          selectedDeviceIds,
        }));

        navigate('/groupPolicy/MaintananceGroup');
      }}
    >
      {() => (
        <Form className="p-6 bg-[#1D1D1D] text-white rounded-xl space-y-6">
          <div className="flex gap-4">
            <div className="w-1/2">
              <label className="block mb-1">Group Name*</label>
              <Field
                name="planName"
                type="text"
                className="w-full p-3 rounded-md border border-gray-600"
                placeholder="Enter group name"
                style={{ backgroundColor: '#161617', color: '#fff' }}
                readOnly={!!id || readOnly} // Keep Group Name disabled in edit/view mode
              />
              <ErrorMessage name="planName" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            <div className="w-1/2">
              <label className="block mb-1">Time zone*</label>
              <Field name="timezone" component={TimezoneField} readOnly={readOnly} />
              <ErrorMessage name="timezone" component="div" className="text-red-500 text-sm mt-1" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-1/3">
              <label className="block mb-1">Search Criteria</label>
              <select
                className="p-3 rounded-md border border-gray-600 w-full"
                style={{ backgroundColor: '#161617', color: '#fff' }}
                value={searchCriteria}
                onChange={(e) => {
                  handleCriteriaChange(e.target.value as 'OS Flavour' | 'Active Directory');
                }}
                disabled={readOnly}
              >
                <option value="OS Flavour">OS Flavour</option>
                <option value="Active Directory">Active Directory</option>
              </select>
            </div>

            {/* Render AD controls when AD is selected */}
            {searchCriteria === 'Active Directory' ? (
              <>
                {/* Domain dropdown - only show if multiple domains exist */}
                {Object.keys(activeDirectoryDomainMap).length > 1 && (
                  <div className="w-1/3">
                    <label className="block mb-1">Select Domain</label>
                    <Select
                      isClearable
                      options={Object.keys(activeDirectoryDomainMap).map((domain) => ({ label: domain, value: domain }))}
                      value={selectedADDomain ? { label: selectedADDomain, value: selectedADDomain } : null}
                      onChange={(selected) => setSelectedADDomain(selected?.value || null)}
                      styles={selectStyles}
                      className="w-full"
                      isDisabled={readOnly} // Disable in view mode
                    />
                  </div>
                )}

                {/* Group (OU) dropdown - always show for AD */}
                <div className="w-1/3">
                  <label className="block mb-1">Select Group</label>
                  <Select
                    isMulti
                    options={getADGroupOptions()}
                    value={selectedADGroups.map((g) => ({ label: g, value: g }))}
                    onChange={handleADGroupChange}
                    styles={selectStyles}
                    className="w-full"
                    isDisabled={readOnly} // Disable in view mode
                  />
                </div>
              </>
            ) : (
              /* Original search value dropdown for OS Flavour */
              <div className="w-2/3">
                <label className="block mb-1">Search Value</label>
                <Select
                  isMulti
                  options={osOptions}
                  value={searchValues}
                  onChange={(selected: MultiValue<SelectOption>) => setSearchValues((selected as SelectOption[]) || [])}
                  styles={selectStyles}
                  className="w-full"
                  isDisabled={readOnly} // Disable in view mode
                />
              </div>
            )}

            <button
              type="button"
              onClick={handleShowDevices}
              className="ml-6 px-6 py-2 bg-white text-black rounded-md hover:bg-gray-200"
              disabled={(searchValues.length === 0 && selectedADGroups.length === 0) || readOnly}
              style={{
                borderRadius: '30px',
                minWidth: '180px',
                minHeight: '55px',
              }}
            >
              Show Devices
            </button>
          </div>

          {id && !CreateGroupData.isDefault && (
            <div className="flex items-center mb-4">
              <label className="mr-3 font-semibold">{isGroupActive ? 'Deactivate Group' : 'Activate Group'}</label>
              <button
                type="button"
                disabled={readOnly}
                onClick={handleToggleActive}
                className={`w-12 h-6 rounded-full p-1 flex items-center transition-colors
                 ${isGroupActive ? 'bg-red-500' : 'bg-gray-500'} ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`bg-white w-4 h-4 rounded-full transform transition-transform
                    ${isGroupActive ? 'translate-x-6' : 'translate-x-0'}`}
                />
              </button>
            </div>
          )}

          {showDevices && (
            <div className="overflow-x-auto mt-6">
              <div className="text-normal mb-1">Selected Items</div>
              <table className="min-w-full text-left border-collapse">
                <thead style={{ backgroundColor: '#161617', color: '#fff' }}>
                  <tr>
                    <th
                      className={`p-3 cursor-pointer ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if (readOnly) return; // Disable in view mode
                        if (selectAll) {
                          // Deselect all visible devices
                          const visibleIds = selectedDevices.map((d) => normalizeId(d));
                          setSelectedDeviceIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
                          // remove origins for those
                          setSelectedDeviceOrigins((prev) => {
                            const copy = { ...prev };
                            visibleIds.forEach((id) => delete copy[id]);
                            return copy;
                          });
                        } else {
                          // Select all visible devices - mark them as manual (don't overwrite existing)
                          const visibleIds = selectedDevices.map((d) => normalizeId(d)).filter(Boolean);
                          setSelectedDeviceIds((prev) => {
                            const merged = Array.from(new Set([...prev, ...visibleIds]));
                            return merged;
                          });
                          setSelectedDeviceOrigins((prev) => {
                            const copy = { ...prev };
                            visibleIds.forEach((id) => {
                              if (!copy[id] || copy[id].origin !== 'existing') {
                                copy[id] = { origin: 'manual', criteria: searchCriteria };
                              }
                            });
                            return copy;
                          });
                        }
                        setSelectAll(!selectAll);
                      }}
                    >
                      <div className={`w-5 h-5 rounded-sm border ${selectAll ? 'bg-green-500 border-green-400' : 'border-gray-500'}`} />
                    </th>

                    <th className="p-3">Device Name</th>
                    <th className="p-3">Operating System</th>
                    <th className="p-3">OS Version</th>
                    <th className="p-3">IP Address</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDevices.map((device, index) => {
                    const id = normalizeId(device);
                    const isSelected = id ? selectedDeviceIds.includes(id) : false;
                    const isInCurrentGroup = device.groupName === currentGroupName;

                    // Avoid shadowing and keep for any display needs
                    const tenantDefaultGroup = localStorage.getItem('defaultTenantGroupName') || '';
                    const isDeviceInDefaultGroup = device.groupName === tenantDefaultGroup;

                    // Disable when viewing read-only, or when trying to newly select an item already in this group
                    const isDisabled = readOnly || (!isSelected && isInCurrentGroup);

                    return (
                      <tr key={id || `row-${index}`} className={`${index % 2 === 0 ? 'bg-[#232222]' : 'bg-[#161617]'}`}>
                        <td
                          className={`p-3 ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (isDisabled) return;
                            handleSquareClick(device);
                          }}
                          title={isInCurrentGroup && !isSelected ? 'Device is already in this group' : ''}
                        >
                          <div
                            className={`w-5 h-5 rounded-sm border ${isSelected ? 'bg-green-500 border-green-400' : 'border-gray-500'} ${isInCurrentGroup && !isSelected ? 'opacity-50' : ''}`}
                          />
                        </td>
                        <td className="p-3">{device.deviceName}</td>
                        <td className="p-3">{device.os}</td>
                        <td className="p-3">{device.osVersion}</td>
                        <td className="p-3">{heartbeatData[normalizeId(device)]?.ipAddress || '-'}</td>
                        <td className="p-3">
                          <AgentStatusIndicator
                            agentId={normalizeId(device)}
                            isActive={heartbeatData[normalizeId(device)]?.status === 'Active'}
                            statusMessage={heartbeatData[normalizeId(device)]?.heartbeatStatusMessage}
                            isLoading={heartbeatLoading}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={heartbeatLoading}
                onPageChange={(page: number) => setCurrentPage(page)}
              />
            </div>
          )}

          <div className="flex justify-start gap-4 mt-6">
            <button
              type="button"
              className="px-5 py-2 bg-gray-700 text-white rounded-full border border-gray-500 hover:bg-gray-600"
              onClick={() => navigate('/Group')}
            >
              Discard
            </button>
            <button type="submit" className="px-5 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 font-semibold">
              Next
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CreateGroupForm;
